// Iter68 — Scanner Cockpit Inventory Tile.
//
// Pure-static, read-only Dashboard tile summarising the completed
// ARXMENTIS scanner cockpit. It enumerates every live scanner
// (Wheel ITM passthrough + BPCS, BCCS, Iron Condor, CFM-CC-SHARES,
// CFM-CC-LEAPS) along with the market condition each one targets,
// its endpoint path, and the globally enforced read-only / no-
// execution / manual-run invariants. It also surfaces the deferred
// capabilities that remain explicitly blocked.
//
// HARD CONSTRAINTS (asserted by tests):
//  * Zero outbound network activity. No HTTP client import.
//  * Tile does NOT run any scanner.
//  * Tile does NOT call the Wheel ITM candidates endpoint.
//  * Tile does NOT call any module preview / scanner / journal /
//    broker / account / position / scan-run endpoint.
//  * Tile contains NO trade / order / execute action.
//  * Endpoint paths are rendered as readable text only.
//
// Static testid registry (asserted by Iter68 tests):
//   scanner-cockpit-scanner-WHEEL_ITM_CRSP
//   scanner-cockpit-scanner-BPCS
//   scanner-cockpit-scanner-BCCS
//   scanner-cockpit-scanner-IRON_CONDOR
//   scanner-cockpit-scanner-CFM_CC_SHARES
//   scanner-cockpit-scanner-CFM_CC_LEAPS

import React from "react";
import {
  Radar, ShieldCheck, EyeOff, BanknoteArrowDown, Hand,
  Lock, AlertOctagon, MousePointerClick, FileX, Palette,
} from "lucide-react";
import {
  STATUS_COLOR_MAP, STATUS_NEUTRAL_DEFAULT,
} from "@/components/ScannerResultNormalization";

// ── Static status legend (Iter76) ────────────────────────────────────
//
// Display-only reference of every status code the toolbox scanners
// can emit, paired with its canonical colour swatch from the shared
// Iter73 STATUS_COLOR_MAP. Each row carries a plain-language
// description so the operator never has to rely on colour alone to
// understand a scanner result — meeting the cockpit's accessibility
// rule: colour is never the only status indicator.

const STATUS_LEGEND = [
  { code: "ENTER",
    note: "Full entry criteria met.",
    bucket: "ACTIONABLE" },
  { code: "WATCH",
    note: "Structure forming, not actionable yet.",
    bucket: "MONITOR" },
  { code: "BLOCKED",
    note: "Hard gate failed (e.g. bar not closed).",
    bucket: "MONITOR" },
  { code: "NO_TRADE",
    note: "Clean evaluation, no actionable structure. Stand down.",
    bucket: "DORMANT" },
  { code: "ERROR",
    note: "Payload malformed or missing — no evaluation performed.",
    bucket: "DEFENSIVE" },
  { code: "DEFEND",
    note: "CFM-Shares — active defensive management warranted.",
    bucket: "DEFENSIVE" },
  { code: "BUYBACK",
    note: "Short call near max profit — buyback candidate.",
    bucket: "MANAGE" },
  { code: "ROLL_OUT_UP",
    note: "Short call should be rolled out + up.",
    bucket: "MANAGE" },
  { code: "ALLOW_ASSIGNMENT",
    note: "CFM-Shares — assignment acceptable vs. basis.",
    bucket: "MANAGE" },
  { code: "ASSIGNMENT_RISK",
    note: "CFM-LEAPS — short call at assignment risk. Diagnostic.",
    bucket: "DEFENSIVE" },
  { code: "DEFEND_LEAPS",
    note: "CFM-LEAPS — long LEAPS itself needs attention.",
    bucket: "DEFENSIVE" },
  { code: "CLOSE_DIAGONAL",
    note: "CFM-LEAPS — full diagonal close is the cleanest path.",
    bucket: "MANAGE" },
];

function StatusLegendRow({ entry }) {
  const cls = STATUS_COLOR_MAP[entry.code] || STATUS_NEUTRAL_DEFAULT;
  return (
    <li
      data-testid={`scanner-cockpit-status-legend-${entry.code}`}
      className="flex items-baseline gap-2 py-1"
    >
      <span
        className={`px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.14em] ${cls}`}
        aria-label={`Status ${entry.code}: ${entry.note}`}
      >
        {entry.code}
      </span>
      <span
        data-testid={`scanner-cockpit-status-legend-bucket-${entry.code}`}
        className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] min-w-[5rem]"
      >
        {entry.bucket}
      </span>
      <span className="text-[10px] font-mono text-[#E0E0E0]">
        {entry.note}
      </span>
    </li>
  );
}

// ── Static scanner inventory metadata (display-only) ──────────────────
//
// Every endpoint here is plain TEXT. The individual scanner panels
// own the live calls, one panel per click. This tile never builds
// a request URL from these entries.

const SCANNERS = [
  {
    scanner_id:           "wheel_itm_crsp",
    module_id:            "WHEEL_ITM_CRSP",
    display_name:         "Wheel ITM / CRSP",
    role:                 "Flagship engine passthrough",
    endpoint:             "/api/forge/candidates",
    frontend_panel:       "WheelITMScannerPanel",
    market_condition:     "Flagship opportunity",
    market_condition_id:  "WHEEL_FLAGSHIP",
    accent:               "#00FF88",     // green — flagship
    description:
      "Protected flagship Wheel ITM / CRSP. Existing engine output, " +
      "rendered read-only.",
  },
  {
    scanner_id:           "bpcs_scanner",
    module_id:            "BPCS",
    display_name:         "BPCS — Bull Put Credit Spread",
    role:                 "Module-specific scanner + manual panel",
    endpoint:             "/api/forge/toolbox/scanner/bpcs-preview",
    frontend_panel:       "BPCSScannerPanel",
    market_condition:     "Support acceptance",
    market_condition_id:  "SUPPORT_ACCEPTANCE",
    accent:               "#007AFF",     // blue
    description:
      "Bullish, defined-risk put credit spread when Wheel ITM is " +
      "not actionable and WES-P confirms support.",
  },
  {
    scanner_id:           "bccs_scanner",
    module_id:            "BCCS",
    display_name:         "BCCS — Bear Call Credit Spread",
    role:                 "Module-specific scanner + manual panel",
    endpoint:             "/api/forge/toolbox/scanner/bccs-preview",
    frontend_panel:       "BCCSScannerPanel",
    market_condition:     "Resistance rejection",
    market_condition_id:  "RESISTANCE_REJECTION",
    accent:               "#FF3366",     // red
    description:
      "Bearish, defined-risk call credit spread when WES-C confirms " +
      "rejection from value-area high or a call wall.",
  },
  {
    scanner_id:           "iron_condor_scanner",
    module_id:            "IRON_CONDOR",
    display_name:         "Iron Condor",
    role:                 "Module-specific scanner + manual panel",
    endpoint:             "/api/forge/toolbox/scanner/iron-condor-preview",
    frontend_panel:       "IronCondorScannerPanel",
    market_condition:     "Value compression / range",
    market_condition_id:  "VALUE_COMPRESSION",
    accent:               "#FFB800",     // amber
    description:
      "Neutral, range-harvesting 4-leg defined-risk structure when " +
      "neither one-sided thesis dominates.",
  },
  {
    scanner_id:           "cfm_shares_scanner",
    module_id:            "CFM_CC_SHARES",
    display_name:         "CFM-CC-SHARES — Cash Flow Machine (Shares)",
    role:                 "Module-specific scanner + manual panel",
    endpoint:             "/api/forge/toolbox/scanner/cfm-shares-preview",
    frontend_panel:       "CFMSharesScannerPanel",
    market_condition:     "Real inventory management",
    market_condition_id:  "INVENTORY_REAL",
    accent:               "#22D3CC",     // cyan
    description:
      "Assignment-aware covered calls against real shares — basis " +
      "reduction + premium cushion + extrinsic income.",
  },
  {
    scanner_id:           "cfm_leaps_scanner",
    module_id:            "CFM_CC_LEAPS",
    display_name:         "CFM-CC-LEAPS / PMCC",
    role:                 "Module-specific scanner + manual panel",
    endpoint:             "/api/forge/toolbox/scanner/cfm-leaps-preview",
    frontend_panel:       "CFMLeapsScannerPanel",
    market_condition:     "Synthetic inventory / PMCC management",
    market_condition_id:  "INVENTORY_SYNTHETIC",
    accent:               "#A855F7",     // violet
    description:
      "Poor Man's Covered Call diagonal. Synthetic inventory, NOT " +
      "shares. Theta-carry-managed, never recommends LEAPS exercise.",
  },
];

// Every scanner enforces the same invariants — displayed as a single
// row of badges rather than repeated on each card.
const GLOBAL_INVARIANTS = [
  { id: "read-only",         label: "Read-only",
    testid: "scanner-cockpit-invariant-read-only" },
  { id: "no-execution",      label: "No execution",
    testid: "scanner-cockpit-invariant-no-execution" },
  { id: "no-broker-orders",  label: "No broker orders",
    testid: "scanner-cockpit-invariant-no-broker-orders" },
  { id: "no-journal-writes", label: "No journal writes",
    testid: "scanner-cockpit-invariant-no-journal-writes" },
  { id: "no-auto-scan",      label: "No auto-scan",
    testid: "scanner-cockpit-invariant-no-auto-scan" },
  { id: "manual-run-only",   label: "Manual-run only",
    testid: "scanner-cockpit-invariant-manual-run-only" },
];

// Deferred / still-blocked capabilities — display only.
const DEFERRED = [
  { id: "full_toolbox_scanner",
    label: "Full toolbox scanner",
    reason: "Cross-module scan disallowed at this phase." },
  { id: "cross_module_orchestrator",
    label: "Cross-module orchestrator",
    reason: "Explicitly disallowed until later phases." },
  { id: "strategy_ranking",
    label: "Strategy ranking across modules",
    reason: "Explicitly disallowed until later phases." },
  { id: "trade_execution",
    label: "Trade execution",
    reason: "Explicitly disallowed." },
  { id: "journal_writes_from_toolbox",
    label: "Journal writes from toolbox",
    reason: "Explicitly disallowed." },
  { id: "snapshot_persistence",
    label: "Snapshot persistence",
    reason: "User authorization pending." },
  { id: "replay_helpers",
    label: "Replay helpers",
    reason: "User authorization pending." },
  { id: "broker_account_live_data_fetch",
    label: "Broker / account live-data fetch",
    reason: "Disallowed in toolbox surface." },
];

// ── Sub-components ────────────────────────────────────────────────────

function InvariantBadge({ inv }) {
  return (
    <span
      data-testid={inv.testid}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#00FF88]"
    >
      <ShieldCheck size={10} />
      {inv.label}
    </span>
  );
}

function ScannerCard({ s }) {
  return (
    <li
      data-testid={`scanner-cockpit-scanner-${s.module_id}`}
      className="rounded-sm border bg-[#08080A] p-3 flex flex-col gap-2"
      style={{ borderColor: `${s.accent}40` }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Radar size={12} style={{ color: s.accent }} />
        <span
          data-testid={`scanner-cockpit-scanner-name-${s.module_id}`}
          className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]"
        >
          {s.display_name}
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-module-id-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-[0.12em]"
          style={{
            borderColor: `${s.accent}66`,
            backgroundColor: `${s.accent}15`,
            color: s.accent,
          }}
        >
          {s.module_id}
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-status-${s.module_id}`}
          className="ml-auto px-1.5 py-0.5 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/10 text-[9px] font-mono uppercase tracking-[0.14em] text-[#00FF88]"
        >
          Live
        </span>
      </div>

      <div className="text-[10px] font-mono text-[#8A8A93] leading-relaxed">
        {s.description}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono">
        <div className="flex items-center gap-1">
          <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
            Endpoint
          </span>
          <code
            data-testid={`scanner-cockpit-scanner-endpoint-${s.module_id}`}
            className="text-[#E0E0E0] truncate"
            title={s.endpoint}
          >
            {s.endpoint}
          </code>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
            Panel
          </span>
          <code
            data-testid={`scanner-cockpit-scanner-panel-${s.module_id}`}
            className="text-[#E0E0E0] truncate"
          >
            {s.frontend_panel}
          </code>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
            Condition
          </span>
          <span
            data-testid={`scanner-cockpit-scanner-condition-${s.module_id}`}
            className="text-[#E0E0E0]"
          >
            {s.market_condition}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
            Role
          </span>
          <span className="text-[#E0E0E0]">{s.role}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 text-[9px] font-mono">
        <span
          data-testid={`scanner-cockpit-scanner-flag-readonly-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88] uppercase tracking-[0.12em]"
        >
          read_only: true
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-flag-execution-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          execution_enabled: false
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-flag-journal-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          journal_mutation_enabled: false
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-flag-broker-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          broker_execution_enabled: false
        </span>
        <span
          data-testid={`scanner-cockpit-scanner-flag-manual-${s.module_id}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88] uppercase tracking-[0.12em]"
        >
          manual_run_only: true
        </span>
      </div>
    </li>
  );
}

function ConditionRow({ s }) {
  return (
    <li
      data-testid={`scanner-cockpit-condition-row-${s.module_id}`}
      className="flex items-center gap-2 text-[10px] font-mono py-1"
    >
      <span className="text-[#5F5F66] min-w-[140px]">
        {s.market_condition}
      </span>
      <span className="text-[#5F5F66]">→</span>
      <span
        className="px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em]"
        style={{
          borderColor: `${s.accent}66`,
          backgroundColor: `${s.accent}15`,
          color: s.accent,
        }}
      >
        {s.display_name}
      </span>
    </li>
  );
}

function DeferredRow({ d }) {
  return (
    <li
      data-testid={`scanner-cockpit-deferred-${d.id}`}
      className="flex items-start gap-2 py-1 text-[10px] font-mono"
    >
      <FileX size={11} className="text-[#5F5F66] mt-0.5 shrink-0" />
      <div className="flex-1">
        <span className="text-[#E0E0E0]">{d.label}</span>
        <span className="text-[#5F5F66]"> · {d.reason}</span>
      </div>
      <span className="px-1.5 py-0.5 rounded-sm border border-[#5F5F66]/40 bg-[#121216] text-[9px] uppercase tracking-[0.12em] text-[#8A8A93] shrink-0">
        Deferred
      </span>
    </li>
  );
}

// ── Tile ──────────────────────────────────────────────────────────────

export default function ScannerCockpitTile() {
  return (
    <section
      data-testid="scanner-cockpit-tile"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[#26262B]">
        <Radar size={16} className="text-[#00FF88]" />
        <div className="flex flex-col">
          <span
            data-testid="scanner-cockpit-title"
            className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]"
          >
            ARXMENTIS Scanner Cockpit
          </span>
          <span
            data-testid="scanner-cockpit-subtitle"
            className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93]"
          >
            Read-only opportunity radar · Iter68 · static inventory · no auto-scan
          </span>
        </div>
        <span
          data-testid="scanner-cockpit-count"
          className="ml-auto text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]"
        >
          {SCANNERS.length} scanners live
        </span>
      </header>

      {/* Global invariants */}
      <div
        data-testid="scanner-cockpit-invariants"
        className="px-4 py-2 border-b border-[#26262B] bg-[#00FF88]/5 flex items-center gap-2 flex-wrap"
      >
        {GLOBAL_INVARIANTS.map(inv => (
          <InvariantBadge key={inv.id} inv={inv} />
        ))}
      </div>

      {/* Scanner inventory grid */}
      <div className="px-4 py-3 border-b border-[#26262B]">
        <div className="flex items-center gap-2 mb-2">
          <MousePointerClick size={11} className="text-[#5F5F66]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
            Scanner inventory
          </span>
        </div>
        <ul
          data-testid="scanner-cockpit-inventory"
          className="grid grid-cols-1 lg:grid-cols-2 gap-2"
        >
          {SCANNERS.map(s => (
            <ScannerCard key={s.module_id} s={s} />
          ))}
        </ul>
      </div>

      {/* Market-condition map */}
      <div className="px-4 py-3 border-b border-[#26262B]">
        <div className="flex items-center gap-2 mb-2">
          <Hand size={11} className="text-[#5F5F66]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
            Market-condition map
          </span>
        </div>
        <ul
          data-testid="scanner-cockpit-condition-map"
          className="divide-y divide-[#26262B]/60"
        >
          {SCANNERS.map(s => (
            <ConditionRow key={s.module_id} s={s} />
          ))}
        </ul>
      </div>

      {/* Status legend (Iter76) — shared status vocabulary */}
      <div className="px-4 py-3 border-b border-[#26262B]">
        <div className="flex items-center gap-2 mb-2">
          <Palette size={11} className="text-[#FFB800]" />
          <span
            data-testid="scanner-cockpit-status-legend-heading"
            className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]"
          >
            Status legend · shared vocabulary across every scanner
          </span>
        </div>
        <ul
          data-testid="scanner-cockpit-status-legend"
          className="space-y-0.5"
        >
          {STATUS_LEGEND.map(entry => (
            <StatusLegendRow key={entry.code} entry={entry} />
          ))}
        </ul>
        <p
          data-testid="scanner-cockpit-status-legend-disclaimer"
          className="mt-2 text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]"
        >
          Static reference. Colour is never the only status indicator —
          every chip also carries its text code, bucket name, and a
          plain-language description.
        </p>
      </div>

      {/* Deferred / blocked */}
      <div className="px-4 py-3 border-b border-[#26262B]">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={11} className="text-[#5F5F66]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
            Deferred / still blocked
          </span>
        </div>
        <ul
          data-testid="scanner-cockpit-deferred-list"
          className="divide-y divide-[#26262B]/60"
        >
          {DEFERRED.map(d => (
            <DeferredRow key={d.id} d={d} />
          ))}
        </ul>
      </div>

      {/* Footer disclaimer */}
      <footer
        data-testid="scanner-cockpit-disclaimer"
        className="px-4 py-2 flex items-center gap-2 text-[10px] font-mono text-[#8A8A93]"
      >
        <AlertOctagon size={11} className="text-[#FFB800] shrink-0" />
        <span>
          This tile does not run scanners. Use each manual panel to run
          one scanner at a time.
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em]">
          <EyeOff size={10} />
          No live broker / account / journal fetch.
          <BanknoteArrowDown size={10} className="ml-1" />
          No trade execution.
        </span>
      </footer>
    </section>
  );
}
